/**
 * Los nombres de los grupos, en el DOM y por encima del canvas.
 *
 * Antes se dibujaban con el resto del grupo dentro de Pixi, pero el recuadro
 * del grupo tiene que ir DEBAJO de las tarjetas (si no, su tinte las taparía) y
 * la etiqueta arrastraba esa misma capa: cualquier tarjeta que cayera encima
 * del borde superior le comía el nombre. Acá se resuelve como la barra de
 * acciones de una tarjeta: un elemento absoluto posicionado en píxeles de pantalla,
 * siempre arriba de todo y con el texto nítido del navegador.
 *
 * Los elementos son `pointer-events: none` a propósito: el click tiene que
 * seguir llegando al canvas para seleccionar y arrastrar el grupo desde el
 * nombre. El hit-test lo hace `hit()` con el rectángulo medido del elemento.
 */

export interface GroupLabelItem {
  id: string;
  /** Esquina superior izquierda del grupo, en coordenadas de mundo. */
  x: number;
  y: number;
  name: string;
  /** Color del grupo, ya resuelto a un entero 0xRRGGBB. */
  color: number;
  selected: boolean;
  /** El arrastre en curso soltaría tarjetas dentro de este grupo. */
  dropTarget: boolean;
  /** `true` compensa el zoom y deja el título a tamaño de pantalla. */
  autoScale: boolean;
  /** Se oculta mientras se renombra: ahí manda el input. */
  hidden: boolean;
}

/**
 * Separación entre la etiqueta y el borde superior del grupo. Va adentro del
 * `transform`, o sea en px de la etiqueta: a escala de mundo el aire crece con
 * ella y el nombre nunca se despega del recuadro.
 */
export const GROUP_LABEL_GAP_PX = 6;

/** Lo último escrito en el DOM de una etiqueta, para no tocarlo en cada frame. */
interface LabelEl {
  el: HTMLElement;
  left: number;
  top: number;
  scale: number;
}

export class GroupLabels {
  readonly root: HTMLElement;
  private els = new Map<string, LabelEl>();

  constructor() {
    this.root = document.createElement("div");
    this.root.className = "group-labels";
  }

  /** Crea, actualiza y descarta etiquetas para que reflejen el estado actual. */
  sync(
    items: GroupLabelItem[],
    worldToScreen: (x: number, y: number) => { x: number; y: number },
    zoom: number,
  ): void {
    const live = new Set<string>();

    for (const item of items) {
      const visible = !item.hidden && item.name.length > 0;
      if (!visible) continue;
      live.add(item.id);

      let entry = this.els.get(item.id);
      if (!entry) {
        const el = document.createElement("div");
        el.className = "group-label";
        entry = { el, left: NaN, top: NaN, scale: NaN };
        this.els.set(item.id, entry);
        this.root.appendChild(el);
      }
      const el = entry.el;

      if (el.textContent !== item.name) el.textContent = item.name;

      const hex = `#${item.color.toString(16).padStart(6, "0")}`;
      if (el.style.getPropertyValue("--group-color") !== hex) {
        el.style.setProperty("--group-color", hex);
      }
      el.classList.toggle("is-selected", item.selected);
      el.classList.toggle("is-drop-target", item.dropTarget);

      // El ancla es la esquina del grupo, en px de pantalla; el tamaño lo pone
      // el `transform`, la misma receta de `CanvasTextLabels`. La escala 1 del
      // modo auto es exactamente lo que hacía la etiqueta antes de que el
      // título pudiera ser parte del recuadro.
      const p = worldToScreen(item.x, item.y);
      const left = Math.round(p.x);
      const top = Math.round(p.y);
      const scale = item.autoScale ? 1 : zoom;
      if (left !== entry.left || top !== entry.top || scale !== entry.scale) {
        entry.left = left;
        entry.top = top;
        entry.scale = scale;
        el.style.left = `${left}px`;
        el.style.top = `${top}px`;
        // El orden importa: primero sube la etiqueta sobre el borde (en su
        // propio espacio, así el aire también escala) y recién después escala.
        el.style.transform = `scale(${scale}) translateY(calc(-100% - ${GROUP_LABEL_GAP_PX}px))`;
      }
    }

    for (const [id, entry] of this.els) {
      if (live.has(id)) continue;
      entry.el.remove();
      this.els.delete(id);
    }
  }

  /** Rectángulo de la etiqueta en píxeles de pantalla, para el hit-test. */
  rect(id: string): { x: number; y: number; width: number; height: number } | null {
    const el = this.els.get(id)?.el;
    if (!el) return null;
    // Medido y no calculado: `offsetWidth`/`offsetHeight` ignoran el
    // `transform`, y a escala de mundo la caja dibujada no es la maquetada.
    // `getBoundingClientRect` ya la trae escalada; se pasa a coordenadas de la
    // capa, que es en las que llega el punto.
    const box = el.getBoundingClientRect();
    const host = this.root.getBoundingClientRect();
    return {
      x: box.left - host.left,
      y: box.top - host.top,
      width: box.width,
      height: box.height,
    };
  }

  /** ¿Cae este punto de pantalla sobre la etiqueta? */
  hit(id: string, screenX: number, screenY: number): boolean {
    const r = this.rect(id);
    if (!r) return false;
    return (
      screenX >= r.x && screenX <= r.x + r.width && screenY >= r.y && screenY <= r.y + r.height
    );
  }

  destroy(): void {
    for (const entry of this.els.values()) entry.el.remove();
    this.els.clear();
    this.root.remove();
  }
}
